import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { FaMusic, FaPause } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { screenSizes } from '../../lib/constants'
import '../../i18n'

const SONG_SRC = '/audio/school-song.m4a'
const SIZE = 60
const RING_STROKE = 4
const RING_RADIUS = (SIZE - RING_STROKE) / 2
const RING_LENGTH = 2 * Math.PI * RING_RADIUS

const pulse = keyframes`
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(1.6); opacity: 0; }
`

const bounce = keyframes`
    0%, 100% { transform: scaleY(0.3); }
    50% { transform: scaleY(1); }
`

const Wrapper = styled.div`
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: ${theme.fontFamily};
    transition: transform 0.3s ease;
    /* Lift the player above the cookie banner while it is shown (set by CookieConsent) */
    transform: translateY(calc(-1 * var(--cookie-banner-height, 0px)));

    @media (max-width: ${screenSizes.md}px) {
        right: 16px;
        bottom: 16px;
    }
`

const Label = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: ${theme.white};
    color: ${theme.blue};
    border: 2px solid ${theme.blue};
    border-radius: 999px;
    box-shadow: 0 4px 16px rgba(49, 48, 149, 0.2);
    font-weight: 700;
    font-size: 0.95rem;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transform: translateX(12px);
    transition: opacity 0.25s ease, transform 0.25s ease;

    ${({ $visible }) =>
        $visible &&
        css`
            opacity: 1;
            transform: translateX(0);
        `}

    @media (max-width: ${screenSizes.md}px) {
        padding: 8px 14px;
        font-size: 0.85rem;
    }
`

const Text = styled.span`
    display: flex;
    flex-direction: column;
    line-height: 1.2;
`

const Credit = styled.span`
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.8;
`

const Equalizer = styled.span`
    display: inline-flex;
    align-items: flex-end;
    gap: 2px;
    height: 14px;

    span {
        width: 3px;
        height: 100%;
        background: ${theme.blue};
        border-radius: 2px;
        transform-origin: bottom;
        transform: scaleY(0.3);
        animation: ${bounce} 0.9s ease-in-out infinite;
        animation-play-state: ${({ $playing }) => ($playing ? 'running' : 'paused')};
    }

    span:nth-child(2) {
        animation-delay: -0.3s;
    }

    span:nth-child(3) {
        animation-delay: -0.6s;
    }
`

const Button = styled.button`
    position: relative;
    width: ${SIZE}px;
    height: ${SIZE}px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: ${theme.blue};
    color: ${theme.yellow};
    font-size: 22px;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(49, 48, 149, 0.35);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: scale(1.06);
        box-shadow: 0 6px 22px rgba(49, 48, 149, 0.45);
    }

    &:focus-visible {
        outline: 3px solid ${theme.lightBlue};
        outline-offset: 3px;
    }

    /* Invite a first click with a soft pulse until the song has been played */
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: ${theme.yellow};
        z-index: -1;
        animation: ${pulse} 2s ease-out infinite;
        display: ${({ $showPulse }) => ($showPulse ? 'block' : 'none')};
    }

    svg.icon {
        position: relative;
    }

    @media (prefers-reduced-motion: reduce) {
        &::before {
            display: none;
        }
    }
`

const Ring = styled.svg`
    position: absolute;
    inset: 0;
    transform: rotate(-90deg);
    pointer-events: none;

    circle {
        fill: none;
        stroke-width: ${RING_STROKE};
    }

    .track {
        stroke: ${theme.yellowTrans};
    }

    .progress {
        stroke: ${theme.yellow};
        stroke-linecap: round;
        transition: stroke-dashoffset 0.25s linear;
    }
`

const SchoolSongPlayer = () => {
    const { t } = useTranslation()
    const audioRef = useRef(null)
    const [playing, setPlaying] = useState(false)
    const [hasPlayed, setHasPlayed] = useState(false)
    const [hovered, setHovered] = useState(false)
    const [progress, setProgress] = useState(0)
    const [isTouch, setIsTouch] = useState(false)

    // Touch screens fire mouseenter/focus on tap and keep them, so there the label follows play/pause only
    useEffect(() => {
        setIsTouch(window.matchMedia('(hover: none)').matches)
    }, [])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const onPlay = () => {
            setPlaying(true)
            setHasPlayed(true)
        }
        const onPause = () => setPlaying(false)
        const onEnded = () => {
            setPlaying(false)
            setProgress(0)
        }
        const onTimeUpdate = () => {
            if (audio.duration) setProgress(audio.currentTime / audio.duration)
        }

        audio.addEventListener('play', onPlay)
        audio.addEventListener('pause', onPause)
        audio.addEventListener('ended', onEnded)
        audio.addEventListener('timeupdate', onTimeUpdate)

        return () => {
            audio.removeEventListener('play', onPlay)
            audio.removeEventListener('pause', onPause)
            audio.removeEventListener('ended', onEnded)
            audio.removeEventListener('timeupdate', onTimeUpdate)
        }
    }, [])

    const toggle = () => {
        const audio = audioRef.current
        if (!audio) return
        if (audio.paused) {
            audio.play().catch(() => setPlaying(false))
        } else {
            audio.pause()
        }
    }

    const label = playing ? t('school-song-pause') : t('school-song-play')

    return (
        <Wrapper>
            <Label $visible={playing || (hovered && !isTouch)} aria-hidden="true">
                <Equalizer $playing={playing}>
                    <span />
                    <span />
                    <span />
                </Equalizer>
                <Text>
                    {t('school-song')}
                    <Credit>{t('school-song-artist')}</Credit>
                </Text>
            </Label>
            <Button
                type="button"
                onClick={toggle}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onFocus={() => setHovered(true)}
                onBlur={() => setHovered(false)}
                aria-label={`${label}: ${t('school-song')} – ${t('school-song-artist')}`}
                aria-pressed={playing}
                title={`${t('school-song')} – ${t('school-song-artist')}`}
                $showPulse={!hasPlayed}
            >
                <Ring width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden="true">
                    <circle className="track" cx={SIZE / 2} cy={SIZE / 2} r={RING_RADIUS} />
                    <circle
                        className="progress"
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={RING_RADIUS}
                        strokeDasharray={RING_LENGTH}
                        strokeDashoffset={RING_LENGTH * (1 - progress)}
                    />
                </Ring>
                {playing ? <FaPause className="icon" /> : <FaMusic className="icon" />}
            </Button>
            {/* preload="none": nothing is downloaded until the visitor presses play,
                then the browser streams the file with range requests */}
            <audio ref={audioRef} src={SONG_SRC} preload="none" />
        </Wrapper>
    )
}

export default SchoolSongPlayer
